/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { ReferralSource } from "../api/referrals";
import { localDayEndIso, localDayStartIso, type FilterField } from "./filters";

/**
 * The derived referral status comes from the legacy rules the referring-provider dashboard also
 * uses, whose baseline bucket is named "Patient Contacted" even though nothing has happened yet.
 * Staff see it as "New"; a stale baseline carries the decay explanation on hover.
 */
export const LEGACY_BASELINE_STATUS = "Patient Contacted";

export type ReferralStatusLabel = { label: string; hint: string | null; decayed: boolean };

const DECAY_AFTER_DAYS = 30;

export function referralStatus(
  status: string | null,
  createdAt?: string | null,
): ReferralStatusLabel {
  if (status === null) {
    return { label: "—", hint: null, decayed: false };
  }
  if (status === LEGACY_BASELINE_STATUS) {
    const decayed = isDecayed(createdAt);
    return {
      label: "New",
      hint: decayed
        ? "No account has been linked in the 30+ days since this referral arrived."
        : null,
      decayed,
    };
  }
  return { label: status, hint: null, decayed: false };
}

function isDecayed(createdAt: string | null | undefined): boolean {
  if (!createdAt) {
    return false;
  }
  const ageMs = Date.now() - new Date(createdAt).getTime();
  return ageMs > DECAY_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

export const SOURCE_LABELS: Record<ReferralSource, string> = {
  FAX: "Fax",
  SCREENER: "Screener",
  REFERRAL_FORM: "Referral form",
};

const STATUS_FILTER_VALUES = [
  { value: "Patient Contacted", label: "New" },
  { value: "Consult Pending", label: "Consult Pending" },
  { value: "Intake Pending - Child Therapy", label: "Intake Pending - Child Therapy" },
  { value: "Active Child Therapy", label: "Active Child Therapy" },
  { value: "Active Parent Therapy", label: "Active Parent Therapy" },
  { value: "Active Parent Navigation", label: "Active Parent Navigation" },
  { value: "Care Not Started", label: "Care Not Started" },
  { value: "Completed Care", label: "Completed Care" },
  { value: "Ineligible", label: "Ineligible" },
  { value: "Pediatrician Review", label: "Pediatrician Review" },
];

export const REFERRAL_FILTER_FIELDS: FilterField[] = [
  {
    key: "status",
    label: "Status",
    kind: "choice",
    searchable: false,
    options: STATUS_FILTER_VALUES,
    scim: (value) => `status eq "${value}"`,
  },
  {
    key: "createdAfter",
    label: "Created after",
    kind: "date",
    scim: (value) => `createdAt ge "@${localDayStartIso(value)}"`,
  },
  {
    key: "createdBefore",
    label: "Created before",
    kind: "date",
    scim: (value) => `createdAt le "@${localDayEndIso(value)}"`,
  },
];
